import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt, Plus, FileText, ExternalLink, Check, Ban, Settings2, Copy, CreditCard } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Modal } from '@/features/admin/Modal';
import { GatewayModal } from './GatewayModal';
import { useEntitlements } from '@/stores/entitlements';
import {
  billingApi,
  STATUS_LABEL,
  type Invoice,
  type InvoiceStatus,
  type BillingConfig,
} from '@/api/endpoints/billing';

const STATUS_CLS: Record<InvoiceStatus, string> = {
  pendente: 'bg-status-ready/15 text-status-ready',
  pago: 'bg-status-running/10 text-status-running',
  vencido: 'bg-status-blocked/10 text-status-blocked',
  cancelado: 'bg-muted text-muted-foreground',
};

function fmtBRL(v: number | null | undefined): string {
  return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function BillingPage() {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const isManager = papel === 'admin' || papel === 'revenda' || papel === 'operador_financeiro';
  if (!isManager) return <ClientView />;
  return <ManagerView />;
}

/* ============================== Cliente ============================== */
function ClientView() {
  const q = useQuery({ queryKey: ['billing', 'my'], queryFn: () => billingApi.myInvoices() });
  const invoices = q.data ?? [];
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-bold">
        <Receipt className="h-5 w-5 text-primary" /> Minhas faturas
      </h1>
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma fatura emitida.</p>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <InvoiceCard key={inv.id} inv={inv} />
          ))}
        </div>
      )}
    </div>
  );
}

function InvoiceCard({ inv }: { inv: Invoice }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-3 p-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{inv.competencia}</span>
            <StatusBadge status={inv.status} />
          </div>
          <p className="text-xs text-muted-foreground">
            {inv.qtd_devices} embarcações · venc. {inv.vencimento ?? '—'}
          </p>
        </div>
        <div className="text-right">
          <p className="font-bold">{fmtBRL(inv.valor)}</p>
        </div>
        <div className="flex gap-2">
          {inv.asaas_invoice_url && (
            <a
              href={inv.asaas_invoice_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Boleto/PIX
            </a>
          )}
          {inv.pix_payload && (
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(inv.pix_payload!);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent"
              title="Copiar código PIX"
            >
              <Copy className="h-3.5 w-3.5" /> PIX
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================== Admin/Revenda ============================== */
function ManagerView() {
  const qc = useQueryClient();
  const me = useEntitlements((s) => s.me);
  // Escopo do gateway: revenda = seu tenant; admin/financeiro = global (0).
  const gatewayScope = me?.user.papel === 'revenda' ? me?.tenant?.id ?? 0 : 0;
  const [competencia, setCompetencia] = useState(thisMonth());
  const [statusFilter, setStatusFilter] = useState('');
  const [genOpen, setGenOpen] = useState(false);
  const [gwOpen, setGwOpen] = useState(false);
  const [cfgTenant, setCfgTenant] = useState<{ id: number; nome: string } | null>(null);

  const clientesQ = useQuery({
    queryKey: ['billing', 'clientes'],
    queryFn: () => billingApi.clientes(),
  });

  const invoicesQ = useQuery({
    queryKey: ['billing', 'invoices', competencia, statusFilter],
    queryFn: () =>
      billingApi.listInvoices({
        competencia: competencia || undefined,
        status: statusFilter || undefined,
      }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['billing'] });

  const payMut = useMutation({ mutationFn: (id: number) => billingApi.pay(id), onSuccess: invalidate });
  const cancelMut = useMutation({ mutationFn: (id: number) => billingApi.cancel(id), onSuccess: invalidate });
  const overdueMut = useMutation({
    mutationFn: () => billingApi.processOverdue(),
    onSuccess: (r) => {
      invalidate();
      alert(`Vencimentos processados:\n• ${r.vencidas} faturas marcadas como vencidas\n• ${r.inadimplentes} clientes marcados inadimplentes.`);
    },
  });

  const invoices = invoicesQ.data ?? [];
  const totals = useMemo(() => {
    let total = 0,
      pago = 0,
      aberto = 0;
    for (const i of invoices) {
      if (i.status === 'cancelado') continue;
      total += i.valor;
      if (i.status === 'pago') pago += i.valor;
      else aberto += i.valor;
    }
    return { total, pago, aberto };
  }, [invoices]);

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold">
            <Receipt className="h-5 w-5 text-primary" /> Cobrança
          </h1>
          <p className="text-sm text-muted-foreground">
            Mensalidade por embarcação. Gere faturas e acompanhe pagamentos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => { if (window.confirm('Marcar faturas pendentes vencidas como "vencido" e os clientes como inadimplentes?')) overdueMut.mutate(); }}
            disabled={overdueMut.isPending}
            className="gap-1.5"
          >
            <Ban className="h-4 w-4" /> {overdueMut.isPending ? 'Processando…' : 'Vencimentos'}
          </Button>
          <Button variant="outline" onClick={() => setGwOpen(true)} className="gap-1.5">
            <CreditCard className="h-4 w-4" /> Gateway
          </Button>
          <Button onClick={() => setGenOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" /> Gerar faturas
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Faturado" value={fmtBRL(totals.total)} />
        <StatCard label="Recebido" value={fmtBRL(totals.pago)} accent="text-status-running" />
        <StatCard label="Em aberto" value={fmtBRL(totals.aberto)} accent="text-status-ready" />
      </div>

      {/* Config por cliente */}
      <Card>
        <CardContent className="p-4">
          <h2 className="mb-2 flex items-center gap-1.5 text-sm font-bold">
            <Settings2 className="h-4 w-4" /> Valor por embarcação (por cliente)
          </h2>
          {clientesQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando clientes…</p>
          ) : (clientesQ.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum cliente cadastrado.</p>
          ) : (
            <ul className="divide-y divide-border">
              {(clientesQ.data ?? []).map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2">
                  <span className="text-sm">{c.nome}</span>
                  <button
                    type="button"
                    onClick={() => setCfgTenant(c)}
                    className="text-xs font-semibold text-primary hover:underline"
                  >
                    Configurar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Filtro de faturas */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold text-muted-foreground">Competência</label>
        <input
          type="month"
          value={competencia}
          onChange={(e) => setCompetencia(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Todos os status</option>
          {(['pendente', 'pago', 'vencido', 'cancelado'] as InvoiceStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      {/* Lista de faturas */}
      <Card>
        <CardContent className="p-0">
          {invoicesQ.isLoading ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Carregando…</p>
          ) : invoices.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Nenhuma fatura nesta competência. Use “Gerar faturas”.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {invoices.map((inv) => (
                <li key={inv.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold">{inv.tenant_nome ?? `#${inv.tenant_id}`}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {inv.competencia} · {inv.qtd_devices} emb. · venc. {inv.vencimento ?? '—'}
                    </p>
                  </div>
                  <span className="font-bold">{fmtBRL(inv.valor)}</span>
                  <div className="flex items-center gap-1">
                    {inv.asaas_invoice_url && (
                      <a
                        href={inv.asaas_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir cobrança Asaas"
                        className="rounded-md p-1.5 text-primary hover:bg-accent"
                      >
                        <FileText className="h-4 w-4" />
                      </a>
                    )}
                    {inv.status !== 'pago' && inv.status !== 'cancelado' && (
                      <button
                        type="button"
                        title="Dar baixa (pago)"
                        onClick={() => {
                          if (window.confirm(`Marcar a fatura de ${inv.tenant_nome} como paga?`)) payMut.mutate(inv.id);
                        }}
                        className="rounded-md p-1.5 text-status-running hover:bg-status-running/10"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                    {inv.status !== 'cancelado' && (
                      <button
                        type="button"
                        title="Cancelar fatura"
                        onClick={() => {
                          if (window.confirm(`Cancelar a fatura de ${inv.tenant_nome}?`)) cancelMut.mutate(inv.id);
                        }}
                        className="rounded-md p-1.5 text-status-blocked hover:bg-status-blocked/10"
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {genOpen && (
        <GenerateModal
          defaultCompetencia={competencia}
          onClose={() => setGenOpen(false)}
          onDone={() => {
            invalidate();
            setGenOpen(false);
          }}
        />
      )}
      {cfgTenant && (
        <ConfigModal tenant={cfgTenant} onClose={() => setCfgTenant(null)} onSaved={() => setCfgTenant(null)} />
      )}
      {gwOpen && (
        <GatewayModal
          tenantId={gatewayScope}
          label={me?.user.papel === 'revenda' ? 'sua revenda' : 'global (TrackMax)'}
          onClose={() => setGwOpen(false)}
        />
      )}
    </div>
  );
}

function GenerateModal({
  defaultCompetencia,
  onClose,
  onDone,
}: {
  defaultCompetencia: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [competencia, setCompetencia] = useState(defaultCompetencia);
  const [emitirAsaas, setEmitirAsaas] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const gen = useMutation({
    mutationFn: () => billingApi.generate({ competencia, emitir_asaas: emitirAsaas }),
    onSuccess: (r) => {
      alert(`Geração ${r.competencia}:\n• ${r.criadas} faturas criadas\n• ${r.puladas} puladas\n• Total ${fmtBRL(r.total)}`);
      onDone();
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal
      title="Gerar faturas"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => { setErr(null); gen.mutate(); }} disabled={gen.isPending}>
            {gen.isPending ? 'Gerando…' : 'Gerar'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-muted-foreground">Competência</span>
          <input
            type="month"
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={emitirAsaas} onChange={(e) => setEmitirAsaas(e.target.checked)} />
          Emitir cobrança no gateway configurado (boleto/PIX)
        </label>
        <p className="text-xs text-muted-foreground">
          Gera 1 fatura por cliente com configuração ativa: nº de embarcações × valor por embarcação.
          Faturas já existentes na competência são puladas.
        </p>
        {err && <p className="text-xs font-medium text-status-blocked">{err}</p>}
      </div>
    </Modal>
  );
}

function ConfigModal({
  tenant,
  onClose,
  onSaved,
}: {
  tenant: { id: number; nome: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const cfgQ = useQuery({
    queryKey: ['billing', 'config', tenant.id],
    queryFn: () => billingApi.getConfig(tenant.id),
  });
  const [form, setForm] = useState<{ valor: string; dia: string; ativo: boolean } | null>(null);
  const cfg: BillingConfig | undefined = cfgQ.data;
  const current = form ?? (cfg
    ? { valor: String(cfg.valor_por_device), dia: String(cfg.dia_vencimento), ativo: cfg.ativo }
    : { valor: '0', dia: '10', ativo: true });

  const save = useMutation({
    mutationFn: () =>
      billingApi.setConfig(tenant.id, {
        valor_por_device: Number(current.valor) || 0,
        dia_vencimento: Number(current.dia) || 10,
        ativo: current.ativo,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing'] });
      onSaved();
    },
  });

  return (
    <Modal
      title={`Cobrança — ${tenant.nome}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || cfgQ.isLoading}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      {cfgQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <div className="space-y-3">
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Valor por embarcação (R$/mês)</span>
            <Input
              type="number"
              step="0.01"
              value={current.valor}
              onChange={(e) => setForm({ ...current, valor: e.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Dia de vencimento (1–28)</span>
            <Input
              type="number"
              min={1}
              max={28}
              value={current.dia}
              onChange={(e) => setForm({ ...current, dia: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={current.ativo}
              onChange={(e) => setForm({ ...current, ativo: e.target.checked })}
            />
            Cobrança ativa para este cliente
          </label>
        </div>
      )}
    </Modal>
  );
}

/* ============================== shared ============================== */
function StatusBadge({ status }: { status: InvoiceStatus }) {
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_CLS[status])}>
      {STATUS_LABEL[status]}
    </span>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
        <p className={cn('text-lg font-bold', accent)}>{value}</p>
      </CardContent>
    </Card>
  );
}
